"use client";

import { useEffect, useRef, useState } from "react";

const steps = [
    {
        number: "01",
        phase: "Initiation",
        title: "NDA & KYC",
        body: "Mutual NDA executed within 48 hours. KYC documentation collected: Certificate of Incorporation, UBO declaration, AML questionnaire. Typical completion: 3–5 business days.",
        duration: "Days 1–5",
    },
    {
        number: "02",
        phase: "Qualification",
        title: "Technical & Commercial Alignment",
        body: "Grade specification confirmed. Indicative volume and delivery schedule agreed. Pricing formula presented (LME-referenced or fixed). Indicative Term Sheet issued.",
        duration: "Days 6–15",
    },
    {
        number: "03",
        phase: "Contracting",
        title: "Offtake Agreement Execution",
        body: "Long-form offtake agreement drafted under English law. Internal legal review period: 5–10 business days. Signing via DocuSign or wet ink. Deposit or LC structure confirmed.",
        duration: "Days 16–30",
    },
    {
        number: "04",
        phase: "First Delivery",
        title: "Pilot Shipment",
        body: "First consignment dispatched from bonded warehouse. Pre-shipment inspection report issued. Documents forwarded within 3 days of B/L date. Customer acceptance confirmed.",
        duration: "Days 31–75",
    },
    {
        number: "05",
        phase: "Steady State",
        title: "Quarterly Delivery Schedule",
        body: "Rolling 12-month forecast updated each quarter. Invoicing on D+3 post B/L. Dedicated account manager reviews performance monthly. Annual audit option available.",
        duration: "Ongoing",
    },
];

export default function WorkflowStepper() {
    const ref = useRef<HTMLDivElement>(null);
    const [activeStep, setActiveStep] = useState(0);

    useEffect(() => {
        const els = ref?.current?.querySelectorAll(".reveal");
        if (!els) return;
        const observer = new IntersectionObserver(
            (entries) =>
                entries.forEach((e) => {
                    if (e.isIntersecting) {
                        e.target.classList.add("visible");
                        observer.unobserve(e.target);
                    }
                }),
            { threshold: 0.08 }
        );
        els?.forEach((el) => observer?.observe(el));
        return () => observer?.disconnect();
    }, []);

    return (
        <section
            ref={ref}
            className="section-navy-mid"
            style={{ padding: "0px 0", paddingBottom: "80px" }}
            aria-labelledby="workflow-heading"
        >
            <div
                className="px-6 lg:px-12"
                style={{ maxWidth: "1280px", margin: "0 auto" }}
            >
                {/* Header */}
                <div
                    className="px-6 lg:px-12 text-center"
                    style={{ maxWidth: "1280px", margin: "0 auto" }}
                >
                    <div
                        className="flex items-center justify-center gap-3 mb-8 reveal"
                    >
                        <span className="type-subheading">Engagement Workflow</span>
                    </div>
                    <h1
                        className="reveal reveal-delay-1"
                        style={{
                            fontFamily: "var(--font-serif)",
                            fontSize: "clamp(1.75rem, 3vw, 2.5rem)",
                            fontWeight: 400,
                            color: "var(--color-white)",
                            lineHeight: 1.05,
                            letterSpacing: "-0.02em",
                            margin: "0 auto",
                        }}
                    >
                        From NDA to steady
                        deliveries in 75 days.
                    </h1>
                    <p
                        className="reveal reveal-delay-2"
                        style={{
                            fontFamily: "var(--font-sans)",
                            fontSize: "1rem",
                            color: "rgba(255,255,255,0.55)",
                            lineHeight: "1.7",
                            marginTop: "20px",
                            margin: "20px auto 0",
                        }}
                    >
                        Our engagement process is designed for procurement professionals:
                        <br />
                        structured, documented, and with clear milestones at every stage.
                    </p>
                </div>

                {/* Stepper */}
                <div className="flex flex-col lg:flex-row gap-8 mt-10">
                    {/* Step List */}
                    <div
                        className="flex flex-col gap-0 reveal"
                        style={{ minWidth: "260px" }}
                        role="tablist"
                        aria-label="Workflow steps"
                    >
                        {steps?.map((step, i) => (
                            <button
                                key={step?.number}
                                role="tab"
                                aria-selected={activeStep === i}
                                aria-controls={`step-panel-${i}`}
                                id={`step-tab-${i}`}
                                onClick={() => setActiveStep(i)}
                                className="text-left flex items-center gap-4 transition-all duration-200"
                                style={{
                                    padding: "16px 20px",
                                    background:
                                        activeStep === i
                                            ? "var(--color-navy)"
                                            : "transparent",
                                    borderLeft: `2px solid ${activeStep === i ? "var(--color-red)" : "var(--color-border)"}`,
                                    cursor: "pointer",
                                    border: "none",
                                }}
                            >
                                <span
                                    style={{
                                        fontFamily: "var(--font-serif)",
                                        fontSize: "1.25rem",
                                        color:
                                            activeStep === i
                                                ? "var(--color-red)"
                                                : "var(--color-muted)",
                                        minWidth: "32px",
                                    }}
                                >
                                    {step?.number}
                                </span>
                                <div>
                                    <p
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.6875rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.1em",
                                            textTransform: "uppercase",
                                            color:
                                                activeStep === i
                                                    ? "var(--color-red)"
                                                    : "var(--color-muted)",
                                            marginBottom: "2px",
                                        }}
                                    >
                                        {step?.phase}
                                    </p>
                                    <p
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.9rem",
                                            fontWeight: 500,
                                            color:
                                                activeStep === i
                                                    ? "#FFFFFF" : "var(--color-navy-mid)",
                                        }}
                                    >
                                        {step?.title}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/* Step Detail Panel */}
                    {steps?.map((step, i) => (
                        <div
                            key={step?.number}
                            id={`step-panel-${i}`}
                            role="tabpanel"
                            aria-labelledby={`step-tab-${i}`}
                            hidden={activeStep !== i}
                            className="flex-1"
                            style={{
                                background: "var(--color-navy)",
                                borderRadius: "4px",
                                padding: "40px 48px",
                            }}
                        >
                            <div className="flex items-start justify-between gap-4 mb-6">
                                <div>
                                    <span
                                        style={{
                                            fontFamily: "var(--font-sans)",
                                            fontSize: "0.6875rem",
                                            fontWeight: 600,
                                            letterSpacing: "0.12em",
                                            textTransform: "uppercase",
                                            color: "var(--color-red)",
                                        }}
                                    >
                                        {step?.phase}
                                    </span>
                                    <h3
                                        style={{
                                            fontFamily: "var(--font-serif)",
                                            fontSize: "1.75rem",
                                            fontWeight: 400,
                                            color: "var(--color-white)",
                                            lineHeight: 1.15,
                                            marginTop: "8px",
                                        }}
                                    >
                                        {step?.title}
                                    </h3>
                                </div>
                                <span
                                    style={{
                                        fontFamily: "var(--font-sans)",
                                        fontSize: "0.75rem",
                                        color: "rgba(255,255,255,0.35)",
                                        background: "rgba(255,255,255,0.06)",
                                        border: "1px solid rgba(255,255,255,0.1)",
                                        padding: "6px 14px",
                                        borderRadius: "2px",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {step?.duration}
                                </span>
                            </div>
                            <p
                                style={{
                                    fontFamily: "var(--font-sans)",
                                    fontSize: "1rem",
                                    color: "rgba(255,255,255,0.62)",
                                    lineHeight: "1.75",
                                }}
                            >
                                {step?.body}
                            </p>

                            {/* Step progress indicator */}
                            <div
                                className="flex items-center gap-2 mt-10"
                                aria-hidden="true"
                            >
                                {steps?.map((_, j) => (
                                    <div
                                        key={j}
                                        style={{
                                            height: "2px",
                                            flex: 1,
                                            background:
                                                j <= activeStep
                                                    ? "var(--color-red)"
                                                    : "rgba(255,255,255,0.1)",
                                            borderRadius: "1px",
                                            transition: "background 250ms",
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}